import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { FluentProvider, webDarkTheme, webLightTheme } from '@fluentui/react-components';
import { AuthProvider } from './context/AuthContext';
import { ConfirmProvider } from './context/ConfirmContext';
import Navigation from './components/Navigation';
import DocumentTitleManager from './components/DocumentTitleManager';
import Dashboard from './components/Dashboard';
import Customers from './components/Customers';
import CustomerDetails from './components/CustomerDetails';
import CustomerEditPage from './components/CustomerEditPage';
import Persons from './components/Persons';
import PersonDetails from './components/PersonDetails';
import PersonEditPage from './components/PersonEditPage';
import Projects from './components/Projects';
import Leads from './components/Leads';
import ProjectDetails from './components/ProjectDetails';
import ProjectEditPage from './components/ProjectEditPage';
import Transactions from './components/Transactions';
import Statistics from './components/Statistics';
import ProfitLoss from './components/ProfitLoss';
import ProjectCategories from './components/ProjectCategories';
import TagsPage from './components/TagsPage';
import ProjectTagsPage from './components/ProjectTagsPage';
import PrivateRoute from './components/PrivateRoute';
import CreateTransactionPage from './components/CreateTransactionPage';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import ActivityForm from './components/ActivityForm';
import Activities from './components/Activities';
import ActivityLogPage from './components/ActivityLogPage';


function App() {
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme === 'dark' || storedTheme === 'light') {
      return storedTheme === 'dark';
    }
    localStorage.setItem('theme', 'light');
    return false;
  });

  const toggleSidebar = () => setSidebarOpen(prev => !prev);
  const closeSidebar = () => setSidebarOpen(false);

  const applyTheme = (dark: boolean) => {
    const root = document.documentElement;
    if (dark) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  const toggleTheme = () => {
    setIsDarkMode(prev => {
      const next = !prev;
      applyTheme(next);
      return next;
    });
  };

  React.useEffect(() => {
    applyTheme(isDarkMode);
  }, [isDarkMode]);

  return (
    <FluentProvider theme={isDarkMode ? webDarkTheme : webLightTheme} className="fluent-app-provider">
      <AuthProvider>
        <ConfirmProvider>
          <Router>
            <DocumentTitleManager />
            <div className={`App ${isSidebarOpen ? 'sidebar-open' : ''}`}>
            <PrivateRoute>
              <div className="main-layout">
                <Navigation
                  isMobileOpen={isSidebarOpen}
                  onToggle={toggleSidebar}
                  onNavigate={closeSidebar}
                  isDarkMode={isDarkMode}
                  onToggleTheme={toggleTheme}
                />
                <div
                  className={`mobile-overlay ${isSidebarOpen ? 'show' : ''}`}
                  onClick={closeSidebar}
                  aria-hidden={!isSidebarOpen}
                />
                <main className="main-content">
                  <div className="main-content-inner">
                    <div className="mobile-header">
                      <button
                        type="button"
                        className="mobile-menu-button"
                        onClick={toggleSidebar}
                        aria-label="Toggle navigation menu"
                        aria-expanded={isSidebarOpen}
                      >
                        <span className="mobile-menu-icon" aria-hidden="true"></span>
                      </button>
                      <div className="mobile-header-title">DevRain Flow</div>
                    </div>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/customers" element={<Customers />} />
                      <Route path="/customers/create" element={<CustomerEditPage />} />
                      <Route path="/customers/edit/:id" element={<CustomerEditPage />} />
                      <Route path="/customers/:id" element={<CustomerDetails />} />
                      <Route path="/persons">
                        <Route index element={<Persons />} />
                        <Route path="new" element={<PersonEditPage />} />
                        <Route path="edit/:id" element={<PersonEditPage />} />
                        <Route path=":id" element={<PersonDetails />} />
                      </Route>
                      <Route path="/projects" element={<Projects />} />
                      <Route path="/leads" element={<Leads />} />
                      <Route path="/projects/create" element={<ProjectEditPage />} />
                      <Route path="/projects/edit/:id" element={<ProjectEditPage />} />
                      <Route path="/projects/:id" element={<ProjectDetails />} />
                      <Route path="/transactions" element={<Transactions />} />
                      <Route path="/transactions/create" element={<CreateTransactionPage />} />
                      <Route path="/statistics" element={<Statistics />} />
                      <Route path="/profitloss" element={<ProfitLoss />} />
                      <Route path="/projectcategories" element={<ProjectCategories />} />
                      <Route path="/tags" element={<TagsPage />} />
                      <Route path="/projecttags" element={<ProjectTagsPage />} />
                      <Route path="/activities/:referenceType/:referenceId/create" element={<ActivityForm />}/>
                      <Route path="/activities/:referenceType/:referenceId/edit/:activityId" element={<ActivityForm />}/>
                      <Route path="/activities" element={<Activities />} />
                      <Route path="/activity-log" element={<ActivityLogPage />} />
                    </Routes>
                  </div>
                </main>
              </div>
            </PrivateRoute>
            </div>
            <ToastContainer />
          </Router>
        </ConfirmProvider>
      </AuthProvider>
    </FluentProvider>
  );
}

export default App;
